/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createSlice } from "@reduxjs/toolkit";
import cloneDeep from "lodash/fp/cloneDeep";

import { ApiErrorCode, RequestStatus } from "~/constants";
import { ComplianceStatus } from "~/features/myAccount/constants";
import {
  createComplianceAsync,
  deleteDocumentAsync,
  deleteNewComplianceFormDocumentAsync,
  finalizeComplianceAsync,
  initComplianceAsync,
  initDocumentsAsync,
  initMyDetailsAsync,
  initPayoutInfoAsync,
  saveMyDetailsAsync,
  savePayoutInfoAsync,
  updateComplianceAsync,
  uploadDocumentAndRefreshDocumentsAsync,
  uploadDocumentAsync,
} from "~/features/myAccount/thunks";
import transformToComplianceDetails from "~/features/myAccount/transformers/transformToComplianceDetails";
import { ComplianceReviewStatus, MyAccountState } from "~/features/myAccount/types";

const initialState: MyAccountState = {
  myDetails: {
    meta: {
      saveStatus: RequestStatus.IDLE,
      initStatus: RequestStatus.IDLE,
    },
    data: {
      email: "",
      phone: "",
    },
  },
  payoutInfo: {
    meta: {
      initStatus: RequestStatus.IDLE,
      saveStatus: RequestStatus.IDLE,
    },
    data: {
      payoutData: null,
      countries: {},
      restrictedCountries: {},
      paymethodInfo: {},
      missingData: [],
    },
  },
  documents: {
    meta: {
      initStatus: RequestStatus.IDLE,
      uploadStatus: RequestStatus.IDLE,
      deleteStatus: {},
    },
    data: {
      pendingDocuments: {
        byId: [],
        ids: [],
      },
      complianceStatus: ComplianceStatus.NOT_FOUND,
      reviewData: null,
      payoutInfo: {
        resellerType: "",
      },
      reviewDocuments: {
        byId: [],
        ids: [],
      },
    },
  },
  compliance: {
    meta: {
      initStatus: RequestStatus.IDLE,
      saveStatus: RequestStatus.IDLE,
      uploadStatus: RequestStatus.IDLE,
      deleteStatus: {},
    },
    data: {
      details: transformToComplianceDetails({ id: 1 }),
      review: { status: ComplianceReviewStatus.UNKNOWN, reviewedAt: "", note: "", reviewId: 0 },
      countries: {},
      documents: {
        pending: [],
        submitted: [],
      },
    },
  },
};

const headerSlice = createSlice({
  name: "myAccount",
  initialState,
  reducers: {
    resetMyDetails: (state) => {
      state.myDetails = cloneDeep(initialState.myDetails);
    },

    resetPayoutInfo: (state) => {
      state.payoutInfo = cloneDeep(initialState.payoutInfo);
    },

    resetDocuments: (state) => {
      state.documents = cloneDeep(initialState.documents);
    },

    resetComplianceDetails: (state) => {
      state.compliance = cloneDeep(initialState.compliance);
    },

    changeStatusToPending: (state) => {
      state.documents.data.complianceStatus = ComplianceStatus.PENDING;
    },
  },
  extraReducers: (builder) => {
    builder
      // My Details Init
      .addCase(initMyDetailsAsync.pending, (state) => {
        state.payoutInfo.meta.initStatus = RequestStatus.PENDING;
      })

      .addCase(initMyDetailsAsync.fulfilled, (state, action) => {
        state.myDetails.meta.initStatus = RequestStatus.SUCCESS;
        state.myDetails.data.email = action.payload.email;
        state.myDetails.data.phone = action.payload.phone;
      })

      .addCase(initMyDetailsAsync.rejected, (state) => {
        state.myDetails.meta.initStatus = RequestStatus.ERROR;
      })

      // My Details Save
      .addCase(saveMyDetailsAsync.pending, (state) => {
        state.myDetails.meta.saveStatus = RequestStatus.PENDING;
      })

      .addCase(saveMyDetailsAsync.fulfilled, (state) => {
        state.myDetails.meta.saveStatus = RequestStatus.SUCCESS;
      })

      .addCase(saveMyDetailsAsync.rejected, (state) => {
        state.myDetails.meta.saveStatus = RequestStatus.ERROR;
      })

      // Payout Info Init
      .addCase(initPayoutInfoAsync.pending, (state) => {
        state.payoutInfo.meta.initStatus = RequestStatus.PENDING;
      })

      .addCase(initPayoutInfoAsync.fulfilled, (state, action) => {
        state.payoutInfo.meta.initStatus = RequestStatus.SUCCESS;
        state.payoutInfo.data.payoutData = action.payload.payoutData;
        state.payoutInfo.data.countries = action.payload.countries;
        state.payoutInfo.data.restrictedCountries = action.payload.restrictedCountries;
        state.payoutInfo.data.paymethodInfo = action.payload.paymethodInfo;
        state.payoutInfo.data.missingData = action.payload.missingData;
        state.documents.data.complianceStatus = action.payload.complianceStatus;
      })

      .addCase(initPayoutInfoAsync.rejected, (state) => {
        state.payoutInfo.meta.initStatus = RequestStatus.ERROR;
      })

      // Payout Info Save
      .addCase(savePayoutInfoAsync.pending, (state) => {
        state.payoutInfo.meta.saveStatus = RequestStatus.PENDING;
      })

      .addCase(savePayoutInfoAsync.fulfilled, (state, action) => {
        state.payoutInfo.meta.saveStatus = RequestStatus.SUCCESS;

        if (Object.values(ApiErrorCode).includes(action.payload.data.code)) {
          state.payoutInfo.meta.saveStatus = RequestStatus.ERROR;
        }
      })

      .addCase(savePayoutInfoAsync.rejected, (state) => {
        state.payoutInfo.meta.saveStatus = RequestStatus.ERROR;
      })

      // Documents Init
      .addCase(initDocumentsAsync.pending, (state) => {
        state.documents.meta.initStatus = RequestStatus.PENDING;
      })

      .addCase(initDocumentsAsync.fulfilled, (state, action) => {
        const { meta, data } = state.documents;
        const { payload } = action;

        meta.initStatus = RequestStatus.SUCCESS;
        data.pendingDocuments = payload.pendingDocuments;
        data.complianceStatus = payload.complianceStatus;
        data.reviewData = payload.reviewData;
        data.reviewDocuments = payload.reviewDocuments;
      })

      .addCase(initDocumentsAsync.rejected, (state) => {
        state.documents.meta.initStatus = RequestStatus.ERROR;
      })

      // Documents Upload
      .addCase(uploadDocumentAsync.pending, (state) => {
        state.documents.meta.uploadStatus = RequestStatus.PENDING;
      })

      .addCase(uploadDocumentAsync.fulfilled, (state, action) => {
        state.documents.meta.uploadStatus = RequestStatus.SUCCESS;
        state.documents.data.pendingDocuments = action.payload;
      })

      .addCase(uploadDocumentAsync.rejected, (state) => {
        state.documents.meta.uploadStatus = RequestStatus.ERROR;
      })

      .addCase(uploadDocumentAndRefreshDocumentsAsync.pending, (state) => {
        state.compliance.meta.uploadStatus = RequestStatus.PENDING;
      })

      .addCase(uploadDocumentAndRefreshDocumentsAsync.fulfilled, (state, action) => {
        state.compliance.meta.uploadStatus = RequestStatus.SUCCESS;
        state.compliance.data.documents = action.payload;
      })

      .addCase(uploadDocumentAndRefreshDocumentsAsync.rejected, (state) => {
        state.compliance.meta.uploadStatus = RequestStatus.ERROR;
      })

      // Documents Delete
      .addCase(deleteDocumentAsync.pending, (state, action) => {
        state.documents.meta.deleteStatus[action.meta.arg] = RequestStatus.PENDING;
      })

      .addCase(deleteDocumentAsync.fulfilled, (state, action) => {
        state.documents.meta.deleteStatus[action.meta.arg] = RequestStatus.SUCCESS;

        const { byId } = state.documents.data.pendingDocuments;

        delete byId[action.meta.arg];
        state.documents.data.pendingDocuments.ids = Object.keys(byId).map(Number);
      })

      .addCase(deleteDocumentAsync.rejected, (state, action) => {
        state.documents.meta.deleteStatus[action.meta.arg] = RequestStatus.ERROR;
      })

      .addCase(deleteNewComplianceFormDocumentAsync.pending, (state, action) => {
        state.compliance.meta.deleteStatus[action.meta.arg] = RequestStatus.PENDING;
      })

      .addCase(deleteNewComplianceFormDocumentAsync.fulfilled, (state, action) => {
        state.compliance.meta.deleteStatus[action.meta.arg] = RequestStatus.SUCCESS;

        const deletedIndex = state.compliance.data.documents.pending.findIndex((doc) => doc.id === action.meta.arg);
        state.compliance.data.documents.pending.splice(deletedIndex, 1);
      })

      .addCase(deleteNewComplianceFormDocumentAsync.rejected, (state, action) => {
        state.compliance.meta.deleteStatus[action.meta.arg] = RequestStatus.ERROR;
      })

      // Compliance Init
      .addCase(initComplianceAsync.pending, (state) => {
        state.compliance.meta.initStatus = RequestStatus.PENDING;
      })

      .addCase(initComplianceAsync.fulfilled, (state, action) => {
        const { meta, data } = state.compliance;
        const { payload } = action;

        meta.initStatus = RequestStatus.SUCCESS;
        data.countries = payload.countries;
        data.details = payload.details;
        data.documents = payload.documents;
        data.review = payload.review;
      })

      .addCase(initComplianceAsync.rejected, (state) => {
        state.compliance.meta.initStatus = RequestStatus.ERROR;
      })

      // Compliance Create
      .addCase(createComplianceAsync.pending, (state) => {
        state.compliance.meta.saveStatus = RequestStatus.PENDING;
      })

      .addCase(createComplianceAsync.fulfilled, (state) => {
        state.compliance.meta.saveStatus = RequestStatus.SUCCESS;
      })

      .addCase(createComplianceAsync.rejected, (state) => {
        state.compliance.meta.initStatus = RequestStatus.ERROR;
      })

      // Compliance Update
      .addCase(updateComplianceAsync.pending, (state) => {
        state.compliance.meta.saveStatus = RequestStatus.PENDING;
      })

      .addCase(updateComplianceAsync.fulfilled, (state) => {
        state.compliance.meta.saveStatus = RequestStatus.SUCCESS;
      })

      .addCase(updateComplianceAsync.rejected, (state) => {
        state.compliance.meta.initStatus = RequestStatus.ERROR;
      })

      // Compliance Finalize
      .addCase(finalizeComplianceAsync.pending, (state) => {
        state.compliance.meta.saveStatus = RequestStatus.PENDING;
      })

      .addCase(finalizeComplianceAsync.fulfilled, (state) => {
        state.compliance.meta.saveStatus = RequestStatus.SUCCESS;
      })

      .addCase(finalizeComplianceAsync.rejected, (state) => {
        state.compliance.meta.initStatus = RequestStatus.ERROR;
      });
  },
});

export const MyAccountActions = headerSlice.actions;

export const myAccountReducer = headerSlice.reducer;
